"use client"

import React from "react"
import Script from "next/script"
import { PaymentMethodROV1 } from "@ub/basket-client"
import { useTranslations } from "next-intl"

import { Button } from "@/components/ui/components"
import CreditCheckConsent from "@/components/checkout/payment/CreditCheckConsent"

interface PayoneCreditCardHostedIFramesProps {
  paymentMethod: PaymentMethodROV1 | undefined
  creditCheckConsentChecked: boolean
  onCreditCheckConsentChange: any
  showCreditCheckConsent: boolean | undefined
  creditCheckConsentText: string | undefined
  showCreditCheckConsentRequired: boolean | undefined
  creditCheckConsentRequiredText: string | undefined
  descriptionText: string | undefined
  disabled?: boolean
}

export default function PayoneCreditCardHostedIFrames(props: PayoneCreditCardHostedIFramesProps) {
  const t = useTranslations("storefront")

  function getParameter(name: string): object | undefined {
    return props.paymentMethod?.hostedPaymentPageParameters?.find((param) => param.name === name)?.value
  }

  const request = getParameter("request") as unknown as string
  const isSecurityCheckCodeRequired = getParameter("isSecurityCheckCodeRequired") as unknown as boolean
  const language = getParameter("language") as unknown as string
  const config = getParameter("config") as unknown as string //.replace(/ } $/, `, language : ${language} } `)

  return (
    <>
      <div>{props.descriptionText ?? ""}</div>
      <Script
        src="https://secure.pay1.de/client-api/js/v1/payone_hosted_min.js"
        strategy={"afterInteractive"}
        onReady={() => {
          try {
            // @ts-ignore
            if (Payone != undefined) {
              const requestO = JSON.parse(request)
              const configO = JSON.parse(config)
              const lang = language.split(".")
              // @ts-ignore
              if (lang.length == 4 && Payone[lang[1]][lang[2]][lang[3]]) {
                // @ts-ignore
                configO["language"] = Payone[lang[1]][lang[2]][lang[3]]
              } else {
                // noinspection ExceptionCaughtLocallyJS
                throw new Error("Unknown language setting")
              }
              // @ts-ignore
              window.iframes = new Payone.ClientApi.HostedIFrames(configO, requestO)
            } else {
              // noinspection ExceptionCaughtLocallyJS
              throw new Error("Payone object not loaded")
            }
          } catch (e) {
            console.error("broken payone config", e)
          }
        }}
      />

      <div className={"grid w-full grid-cols-3 gap-4"}>
        <div className="text-danger col-span-3" id="error"></div>

        <div className={"col-span-3 flex flex-col gap-2"}>
          <h2 className={"text-large font-bold"}>{t("checkout.payment.payone.creditcard.cardtype")}</h2>
          <div id="cardtype"></div>
        </div>

        <div className={"col-span-3 flex flex-col gap-2"}>
          <h2 className={"text-large font-bold"}>{t("checkout.payment.payone.creditcard.cardnumber")}</h2>
          <div id="cardpan"></div>
        </div>
        <div className={"col-span-1 flex flex-row gap-2"}>
          <div>
            <div className={"flex flex-col gap-2"}>
              <h2 className={"text-large font-bold"}>{t("checkout.payment.payone.creditcard.expirationmonth")}</h2>
              <span id="cardexpiremonth"></span>
            </div>
          </div>
          <div>
            <div className={"flex flex-col gap-2"}>
              <h2 className={"text-large font-bold"}>{t("checkout.payment.payone.creditcard.expirationyear")}</h2>
              <span id="cardexpireyear"></span>
            </div>
          </div>
        </div>
        <div className={"col-span-1"}>
          {isSecurityCheckCodeRequired && (
            <div className={"flex flex-col gap-2"}>
              <h2 className={"text-large font-bold"}>{t("checkout.payment.payone.creditcard.cvc")}</h2>
              <div id="cardcvc2"></div>
            </div>
          )}
        </div>

        <div className="col-span-3">
          <div className={"flex flex-col justify-end gap-2 md:flex-row"}>
            <CreditCheckConsent
              checked={props.creditCheckConsentChecked}
              onChange={props.onCreditCheckConsentChange}
              show={props.showCreditCheckConsent}
              text={props.creditCheckConsentText}
              showRequired={props.showCreditCheckConsentRequired}
              requiredText={props.creditCheckConsentRequiredText}
            />
            <Button
              size={"default"}
              variant={"default"}
              className={"cursor-pointer bg-primary py-0 font-normal text-white"}
              id={"payoneCreditCardCheck"}
              type={"button"}
              disabled={props.disabled || (props.showCreditCheckConsent && !props.creditCheckConsentChecked)}
              onClick={() => {
                if (!props.disabled && window.iframes?.isComplete()) {
                  window.iframes.creditCardCheck("payoneCreditCardCallback")
                }
              }}
            >
              {t("checkout.payment.button")}
            </Button>
          </div>
        </div>
      </div>

      <input type={"hidden"} name={"pseudocardpan"} id={"pseudocardpan"} />
      <input type={"hidden"} name={"truncatedcardpan"} id={"truncatedcardpan"} />

      <Script id="payone-hosted-iframes">{`
        function payoneCreditCardCallback(response) {
          if (response.status === "VALID") {
            document.getElementById('paymentMethodID').value = "${props.paymentMethod?.id}";
            document.getElementById('pseudocardpan').value = response.pseudocardpan;
            document.getElementById('truncatedcardpan').value = response.truncatedcardpan;
            document.querySelector('form[name="PaymentForm"]').requestSubmit();
          }
        }
      `}</Script>
    </>
  )
}
